/**
 * PlaythroughProgress — compact header widget that shows the player's live
 * redeemable SC + 1× playthrough progress. Auto-refreshes every 15s so the
 * number climbs while they play. Click to open a full details popover.
 *
 * Hidden until the player has purchased at least some SC (otherwise there's
 * nothing to play through and the widget just adds noise).
 */
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { useAuth, API } from "../App";
import { motion, AnimatePresence } from "framer-motion";
import { TrendingUp, Sparkles, X } from "lucide-react";
import { useNavigate } from "react-router-dom";

const REFRESH_MS = 5000;
// Any game component can fire `window.dispatchEvent(new Event("sc:bet-settled"))`
// after a spin to force an immediate refresh instead of waiting for the poll.
const BET_EVENT = "sc:bet-settled";

const PlaythroughProgress = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [open, setOpen] = useState(false);
  const [flashDelta, setFlashDelta] = useState(0);
  const prevRedeemableRef = useRef(null);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;

    const fetchInfo = async () => {
      try {
        const r = await axios.get(`${API}/balance/info`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        const next = r.data;
        // Flash a "+X.XX SC" tag when the redeemable number climbs (from playing).
        const prev = prevRedeemableRef.current;
        const nextRedeemable = Number(next?.sc_redeemable || 0);
        if (prev != null && nextRedeemable > prev) {
          setFlashDelta(nextRedeemable - prev);
          setTimeout(() => setFlashDelta(0), 3000);
        }
        prevRedeemableRef.current = nextRedeemable;
        setInfo(next);
      } catch (e) {
        /* ignore transient errors — will retry on next tick */
      }
    };

    fetchInfo();
    const id = setInterval(fetchInfo, REFRESH_MS);
    // Event-based instant refresh: games dispatch `sc:bet-settled` after each
    // spin/hand so the Redeemable pill ticks up the moment the bet resolves.
    const onBet = () => fetchInfo();
    window.addEventListener(BET_EVENT, onBet);
    return () => {
      cancelled = true;
      clearInterval(id);
      window.removeEventListener(BET_EVENT, onBet);
    };
  }, [token]);

  // Show for every authenticated user, regardless of whether they have
  // purchased SC yet. Fresh accounts will just see "Redeemable 0.00 SC".
  const lifetimePurchased = Number(info?.total_sc_purchased_lifetime || 0);
  if (!token || !info) return null;

  const redeemable = Number(info?.sc_redeemable || 0);
  const totalWagered = Number(info?.total_wagered || 0);
  const cleared = info?.wager_cleared_1x;
  const multiplier = Number(info?.playthrough_multiplier || 1.5);
  const wagerRequired = Number(info?.wager_required || lifetimePurchased * multiplier);
  const progressPct = Math.min(100, (totalWagered / Math.max(wagerRequired, 1)) * 100);
  const remaining = Math.max(0, wagerRequired - totalWagered);

  return (
    <>
      {/* Compact pill — visible in the header on all screens */}
      <button
        onClick={() => setOpen(true)}
        data-testid="playthrough-progress-pill"
        title="Tap to see your playthrough progress"
        className="flex items-center gap-1 sm:gap-1.5 px-2 sm:px-2.5 py-1 sm:py-1.5 rounded-full bg-gradient-to-r from-emerald-500/10 to-amber-500/10 border border-emerald-500/30 hover:border-emerald-400/60 transition-colors group"
      >
        <TrendingUp className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-emerald-400" />
        <div className="flex items-center gap-1 text-[10px] sm:text-[11px]">
          <span className="hidden sm:inline text-zinc-400">Redeemable</span>
          <span className="font-bold text-emerald-400 tabular-nums">
            {redeemable.toFixed(2)}
          </span>
          <span className="text-zinc-500">SC</span>
        </div>
        {!cleared && (
          <span className="text-[10px] font-semibold text-amber-400 tabular-nums bg-amber-500/10 px-1.5 py-0.5 rounded">
            {progressPct.toFixed(0)}%
          </span>
        )}
        {cleared && (
          <Sparkles className="w-3 h-3 text-amber-400" />
        )}

        {/* Floating "+X.XX SC" flash when redeemable ticks up during play */}
        <AnimatePresence>
          {flashDelta > 0 && (
            <motion.span
              key={flashDelta}
              initial={{ opacity: 0, y: 0 }}
              animate={{ opacity: 1, y: -18 }}
              exit={{ opacity: 0, y: -28 }}
              transition={{ duration: 2.5 }}
              className="absolute pointer-events-none text-[10px] font-bold text-emerald-300 whitespace-nowrap"
              style={{ transform: "translateY(-14px)" }}
            >
              +{flashDelta.toFixed(2)} SC
            </motion.span>
          )}
        </AnimatePresence>
      </button>

      {/* Details popover — rendered via portal so the header's backdrop-filter
          doesn't create a containing block that traps the fixed positioning. */}
      {createPortal(
        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[80] flex items-center justify-center p-4"
              onClick={() => setOpen(false)}
              data-testid="playthrough-popover-backdrop"
            >
              <motion.div
                initial={{ opacity: 0, y: 20, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 20, scale: 0.95 }}
                onClick={(e) => e.stopPropagation()}
                className="relative w-full max-w-[440px] max-h-[90vh] overflow-y-auto bg-gradient-to-b from-zinc-900 to-black border border-emerald-500/30 rounded-2xl shadow-2xl p-5 sm:p-6 z-[81]"
                data-testid="playthrough-popover"
              >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-emerald-400" />
                  <h3 className="text-lg font-black text-white">Playthrough Progress</h3>
                </div>
                <button
                  onClick={() => setOpen(false)}
                  className="text-zinc-500 hover:text-white transition-colors"
                  data-testid="playthrough-popover-close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Big redeemable number */}
              <div className="text-center mb-5">
                <p className="text-xs uppercase tracking-wide text-zinc-500 mb-1">Currently Redeemable</p>
                <p className="text-4xl font-black text-emerald-400 tabular-nums">
                  {redeemable.toFixed(2)}
                  <span className="text-lg text-emerald-500/70 ml-1">SC</span>
                </p>
                <p className="text-[11px] text-zinc-500 mt-1">
                  ≈ ${redeemable.toFixed(2)} USD
                </p>
              </div>

              {/* Progress bar */}
              <div className="mb-5">
                <div className="flex items-center justify-between text-xs mb-1.5">
                  <span className="text-zinc-400">{multiplier}× Playthrough Progress</span>
                  <span className="font-bold text-emerald-400 tabular-nums">
                    {progressPct.toFixed(1)}%
                  </span>
                </div>
                <div className="relative h-3 bg-zinc-800 rounded-full overflow-hidden">
                  <motion.div
                    className={`absolute inset-y-0 left-0 rounded-full ${
                      cleared
                        ? "bg-gradient-to-r from-emerald-500 to-emerald-300"
                        : "bg-gradient-to-r from-amber-500 to-emerald-400"
                    }`}
                    initial={{ width: 0 }}
                    animate={{ width: `${progressPct}%` }}
                    transition={{ duration: 0.6 }}
                  />
                </div>
                <div className="flex items-center justify-between text-[11px] text-zinc-500 mt-1.5 tabular-nums">
                  <span>{totalWagered.toFixed(2)} SC wagered</span>
                  <span>{wagerRequired.toFixed(2)} SC required</span>
                </div>
              </div>

              {/* Status message */}
              {cleared ? (
                <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3 mb-4">
                  <p className="text-sm text-emerald-300 font-semibold flex items-center gap-2">
                    <Sparkles className="w-4 h-4" />
                    Playthrough cleared! Full balance redeemable.
                  </p>
                </div>
              ) : (
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 mb-4">
                  <p className="text-sm text-amber-200 font-semibold mb-1">
                    Wager {remaining.toFixed(2)} SC more to unlock full redemption
                  </p>
                  <p className="text-[11px] text-amber-100/70">
                    Every SC you wager on slots unlocks ${(multiplier > 0 ? (1 / multiplier) : 1).toFixed(2)} more redeemable — updates live as you play.
                  </p>
                </div>
              )}

              {/* Rules note */}
              <p className="text-[11px] text-zinc-500 leading-relaxed mb-4">
                Table games (Blackjack/Roulette) contribute 10%, Poker 25%, and Slots 100% toward playthrough. Bonus/promo SC has separate playthrough requirements.
              </p>

                <div className="flex gap-2">
                  <button
                    onClick={() => { setOpen(false); navigate("/lobby"); }}
                    data-testid="playthrough-play-btn"
                    className="flex-1 h-10 rounded-lg bg-gradient-to-r from-amber-500 to-yellow-400 text-black font-bold text-sm hover:brightness-110 transition"
                  >
                    Play to Unlock More
                  </button>
                  <button
                    onClick={() => { setOpen(false); navigate("/redeem"); }}
                    data-testid="playthrough-redeem-btn"
                    className="flex-1 h-10 rounded-lg border border-emerald-500/50 text-emerald-400 font-bold text-sm hover:bg-emerald-500/10 transition"
                  >
                    Go to Redeem
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </>
  );
};

export default PlaythroughProgress;
